import {
    Card,
    CardHeader,
    makeStyles,
    TableCellLayout,
    type TableColumnDefinition,
    Text,
} from '@fluentui/react-components';
import { memo, useMemo } from 'react';
import { graphql, usePreloadedQuery } from 'react-relay';
import { useMediaQuery } from 'usehooks-ts';

import { createTableColumns } from '../list/column/column-util';
import { QueryList } from '../list/QueryList';
import { ProcessDefinitionListLink } from '../process-definition/process-definition-list-link';

import type { processDefinitionsTemplateQuery } from './__generated__/processDefinitionsTemplateQuery.graphql';
import type { processDefinitionsTemplateQueryFragment$data } from './__generated__/processDefinitionsTemplateQueryFragment.graphql';

import { processDefinitionsTemplateRouteApi } from './process-definitions-template-api';
import {
    ProcessDefinitionsTemplateQueryCtx,
    useProcessDefinitionsTemplateQueryFragmentRef,
} from './process-definitions-template-query';

import { toolbarItems } from './process-definitions-template-toolbar';

export const ProcessDefinitionsTemplate = memo(() => {
    //const isTablet = useMediaQuery('(max-width: 1100px)');
    const isMobile = useMediaQuery('(max-width: 768px)');

    const processDefinitionsTemplate = usePreloadedQuery<processDefinitionsTemplateQuery>(
        query,
        processDefinitionsTemplateRouteApi.useLoaderData()
    ).getProcessDefinitions;

    const queryFragment = useProcessDefinitionsTemplateQueryFragmentRef(processDefinitionsTemplate);

    const items = queryFragment?.content ?? [];

    // 🔥 Tablet: reduzierte Spalten (hier aktuell nur 1 Spalte → bleibt gleich)
    const responsiveColumns = useMemo(() => {
        return columns;
    }, []);

    return (
        <ProcessDefinitionsTemplateQueryCtx.Provider value={processDefinitionsTemplate}>
            {isMobile ? (
                <ProcessDefinitionTemplateCards items={items} />
            ) : (
                <QueryList
                    columns={responsiveColumns}
                    items={items}
                    pagination={queryFragment?.pageInfo}
                    routeApi={processDefinitionsTemplateRouteApi}
                    selectionMode="multiselect"
                    toolbar={toolbarItems}
                />
            )}
        </ProcessDefinitionsTemplateQueryCtx.Provider>
    );
});
ProcessDefinitionsTemplate.displayName = 'ProcessDefinitionsTemplate';

const query = graphql`
    query processDefinitionsTemplateQuery(
        $pageNum: Int
        $pageSize: Int
        $sortBy: String
        $sortDir: SortDirection
        $search: String
        $searchBy: [String]
        $filter: ProcessDefinitionFilterInput
    ) {
        getProcessDefinitions(
            pageNum: $pageNum
            pageSize: $pageSize
            sortBy: $sortBy
            sortDir: $sortDir
            search: $search
            searchBy: $searchBy
            filterInput: $filter
        ) {
            ...processDefinitionsTemplateQueryFragment
        }
    }
`;

type TProcessDefinitionsTemplate =
    processDefinitionsTemplateQueryFragment$data['content'][number][];

type TProcessDefinitionTemplate = TProcessDefinitionsTemplate[number];

const columns: TableColumnDefinition<TProcessDefinitionTemplate>[] =
    createTableColumns<TProcessDefinitionTemplate>(
        {
            columnId: 'id',
            renderCell: (item) => (
                <TableCellLayout>
                    <ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>
                </TableCellLayout>
            ),
            renderHeaderCell: 'Prozessname',
        }
        /*
        Optional später:
        {
            columnId: 'definitionVersion',
            renderHeaderCell: 'Version',
        },
        {
            columnId: 'definitionKey',
            renderHeaderCell: 'Prozess-ID',
        }
        */
    );

const useCardStyles = makeStyles({
    container: {
        display: 'grid',
        gap: '1rem',
        padding: '1rem',
    },
    card: {
        padding: '1rem',
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
});

const ProcessDefinitionTemplateCards = ({
    items,
}: {
    items: readonly TProcessDefinitionTemplate[];
}) => {
    const styles = useCardStyles();

    return (
        <div className={styles.container}>
            {items.map((item) => (
                <Card key={item.id} className={styles.card}>
                    <CardHeader
                        header={<ProcessDefinitionListLink>{item}</ProcessDefinitionListLink>}
                    />

                    {/* Optional erweiterbar falls du später Version / ID anzeigen willst */}
                    {item.definitionVersion && (
                        <div className={styles.row}>
                            <Text size={200}>Version {item.definitionVersion}</Text>
                            <Text size={200}>{item.definitionKey}</Text>
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
};
